import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ArrowLeft, LayoutTemplate, Search, Target, Users } from 'lucide-react'

import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { cn } from 'lib/utils'
import { fetchTemplates } from 'slices/explorerSlice'

import { GRAIN_LABELS, TEMPLATE_TABS, humanizeColumnKey, templateFitsAsset } from '../constants'
import * as explorerService from '../services/explorerService'
import type {
	ExplorerReport,
	ExplorerReportScope,
	ExplorerScopeAsset,
	ExplorerScopeType,
	ExplorerTemplateScope,
} from '../types/explorer.types'

export type TemplateGalleryTab = ExplorerTemplateScope | 'mine'

interface ExplorerTemplateGalleryProps {
	open: boolean
	onClose: () => void
	/**
	 * Opened from an asset's page: only templates that fit that asset are
	 * offered, and choosing one uses it on that asset straight away.
	 */
	boundAsset?: ExplorerScopeAsset | null
	/** The tab to open on when not bound to an asset. */
	initialTab?: TemplateGalleryTab
	/** A template was chosen, with the asset it is to be used on, if any. */
	onUse: (template: ExplorerReport, scope: ExplorerReportScope | null) => void
}

const TABS: Array<{ value: TemplateGalleryTab; label: string }> = [...TEMPLATE_TABS, { value: 'mine', label: 'Mine' }]

const PREVIEW_COLUMNS = 4
const SEARCH_DEBOUNCE_MS = 250

/**
 * Start a report from a template (MYD-598).
 *
 * Two ways in:
 *  - From Explorer, unbound: tabs by the kind of asset a template is used on,
 *    plus Mine. A template used on an asset asks which one before opening.
 *  - From an asset's page, bound: only the templates that fit that asset, and
 *    choosing one opens it on that asset with no further question.
 *
 * Templates are the user's own plus partners' shared ones; the server decides
 * which, with the same rule as shared reports.
 */
export function ExplorerTemplateGallery({
	open,
	onClose,
	boundAsset = null,
	initialTab = 'project',
	onUse,
}: ExplorerTemplateGalleryProps) {
	const dispatch = useDispatch()
	const { templates, templatesLoaded } = useSelector((state: any) => state.explorer) as {
		templates: ExplorerReport[]
		templatesLoaded: boolean
	}

	const [tab, setTab] = useState<TemplateGalleryTab>(initialTab)
	const [picking, setPicking] = useState<ExplorerReport | null>(null)

	// Refetched on every open: a partner may have shared one since.
	useEffect(() => {
		if (open) dispatch(fetchTemplates() as any)
	}, [open, dispatch])

	// Each opening starts fresh, on the tab the caller asked for.
	useEffect(() => {
		if (open) {
			setTab(initialTab)
			setPicking(null)
		}
	}, [open, initialTab])

	const visible = useMemo(() => {
		if (boundAsset) return templates.filter(template => templateFitsAsset(template, boundAsset.type))
		if (tab === 'mine') return templates.filter(template => template.isOwner)
		return templates.filter(template => template.templateScope === tab)
	}, [templates, boundAsset, tab])

	const choose = (template: ExplorerReport) => {
		if (boundAsset) {
			onUse(template, { type: boundAsset.type, id: boundAsset.id })
			return
		}
		// A template used on an asset needs to know which one.
		if (template.definition?.scope?.type) {
			setPicking(template)
			return
		}
		onUse(template, null)
	}

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="sm:max-w-[760px]">
				<DialogHeader>
					<DialogTitle>
						{boundAsset ? `Reports for ${boundAsset.name}` : picking ? `Use “${picking.name}”` : 'New from template'}
					</DialogTitle>
					<DialogDescription>
						{boundAsset
							? 'Pick a layout. It opens in Explorer showing only this ' + boundAsset.type + '’s rows.'
							: picking
								? `Choose the ${picking.definition?.scope?.type} to report on.`
								: 'Start from a saved layout. Rows are always your own.'}
					</DialogDescription>
				</DialogHeader>

				<div className="px-5 py-4">
					{picking && picking.definition?.scope?.type ? (
						<AssetPicker
							type={picking.definition.scope.type}
							onBack={() => setPicking(null)}
							onPick={asset => onUse(picking, { type: asset.type, id: asset.id })}
						/>
					) : (
						<>
							{!boundAsset && (
								<div role="tablist" aria-label="Template kinds" className="mb-4 flex flex-wrap gap-1">
									{TABS.map(option => (
										<button
											key={option.value}
											type="button"
											role="tab"
											aria-selected={tab === option.value}
											onClick={() => setTab(option.value)}
											className={cn(
												'rounded-full px-3 py-1.5 text-sm transition-colors',
												tab === option.value
													? 'bg-primary/10 font-medium text-primary'
													: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
											)}
										>
											{option.label}
										</button>
									))}
								</div>
							)}

							{!templatesLoaded ? (
								<p className="py-10 text-center text-sm text-gray-500">Loading templates…</p>
							) : visible.length === 0 ? (
								<EmptyTemplates bound={Boolean(boundAsset)} />
							) : (
								<div className="grid max-h-[55vh] grid-cols-1 gap-3 overflow-y-auto sm:grid-cols-2" role="list">
									{visible.map(template => (
										<TemplateCard key={template.id} template={template} onChoose={() => choose(template)} />
									))}
								</div>
							)}
						</>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}

function TemplateCard({ template, onChoose }: { template: ExplorerReport; onChoose: () => void }) {
	const columns = template.definition?.columns ?? []
	const shown = columns.slice(0, PREVIEW_COLUMNS).map(humanizeColumnKey)
	const more = columns.length - shown.length
	const bindsTo = template.definition?.scope?.type

	return (
		<button
			type="button"
			role="listitem"
			onClick={onChoose}
			className="flex h-full flex-col gap-2 rounded-lg border border-gray-200 bg-white p-4 text-left transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
		>
			<div className="flex items-start justify-between gap-2">
				<span className="font-medium text-gray-900">{template.name}</span>
				{!template.isOwner && (
					<span title="Shared by a partner" className="shrink-0 text-gray-400">
						<Users className="h-4 w-4" />
					</span>
				)}
			</div>
			{template.description && <p className="line-clamp-2 text-xs text-gray-500">{template.description}</p>}
			<div className="flex flex-wrap items-center gap-1.5">
				<Badge variant="secondary">{GRAIN_LABELS[template.grain] ?? template.grain}</Badge>
				{bindsTo && (
					<Badge variant="outline" className="gap-1">
						<Target className="h-3 w-3" /> Per {bindsTo}
					</Badge>
				)}
			</div>
			{shown.length > 0 && (
				<p className="text-xs text-gray-500">
					{shown.join(' · ')}
					{more > 0 && ` · +${more} more`}
				</p>
			)}
		</button>
	)
}

function EmptyTemplates({ bound }: { bound: boolean }) {
	return (
		<div className="flex flex-col items-center justify-center py-10 text-center">
			<div className="mb-4 rounded-full bg-gray-100 p-3">
				<LayoutTemplate className="h-6 w-6 text-gray-400" />
			</div>
			<h3 className="mb-1 text-lg font-medium text-gray-900">No templates here yet</h3>
			<p className="max-w-sm text-sm text-gray-500">
				{bound
					? 'None of your templates fit this kind of asset yet.'
					: 'Open a saved report in Explorer and choose “Save as template” from its menu.'}
			</p>
		</div>
	)
}

/**
 * Choose the asset a template is used on: the caller's own or partnered assets
 * of one kind, searchable, from the same access rule the report will run with.
 */
function AssetPicker({
	type,
	onBack,
	onPick,
}: {
	type: ExplorerScopeType
	onBack: () => void
	onPick: (asset: ExplorerScopeAsset) => void
}) {
	const [query, setQuery] = useState('')
	const [assets, setAssets] = useState<ExplorerScopeAsset[]>([])
	const [loading, setLoading] = useState(true)
	const [failed, setFailed] = useState(false)

	useEffect(() => {
		let cancelled = false
		setLoading(true)
		const timer = setTimeout(() => {
			explorerService
				.listScopeAssets(type, query.trim() || undefined)
				.then(found => {
					if (cancelled) return
					setAssets(found)
					setFailed(false)
				})
				.catch(() => !cancelled && setFailed(true))
				.finally(() => !cancelled && setLoading(false))
		}, SEARCH_DEBOUNCE_MS)
		return () => {
			cancelled = true
			clearTimeout(timer)
		}
	}, [type, query])

	return (
		<div className="space-y-3">
			<div className="flex items-center gap-2">
				<Button variant="ghost" isOutline onClick={onBack} aria-label="Back to templates">
					<ArrowLeft className="h-4 w-4" />
				</Button>
				<div className="relative flex-1">
					<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
					<Input
						autoFocus
						value={query}
						onChange={event => setQuery(event.target.value)}
						placeholder={`Search your ${type}s`}
						aria-label={`Search your ${type}s`}
						className="pl-9"
					/>
				</div>
			</div>

			<div className="max-h-[45vh] overflow-y-auto rounded-lg border border-gray-200" role="listbox" aria-label={`Your ${type}s`}>
				{loading && assets.length === 0 ? (
					<p className="p-4 text-sm text-gray-500">Loading…</p>
				) : failed ? (
					<p className="p-4 text-sm text-destructive">Could not load your {type}s.</p>
				) : assets.length === 0 ? (
					<p className="p-4 text-sm text-gray-500">No {type}s match.</p>
				) : (
					assets.map(asset => (
						<button
							key={asset.id}
							type="button"
							role="option"
							aria-selected={false}
							onClick={() => onPick(asset)}
							className="block w-full border-b border-gray-100 px-4 py-2.5 text-left text-sm text-gray-900 last:border-b-0 hover:bg-gray-50"
						>
							{asset.name || `Unnamed ${type}`}
						</button>
					))
				)}
			</div>
		</div>
	)
}

export default ExplorerTemplateGallery
